import gql from 'graphql-tag';

import { useAdminMasterClassListViewQuery } from '@/@generated/codegen/API';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { usePaginationState } from '@/hooks/usePaginationState';
import { PageLoading } from '@/ui/PageLoading';
import { usePrevious } from '@/utils/helpers/react.helper';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { AdminMasterclassListView } from './AdminMasterclassListView';
import { useAdminMasterClassListQueryParams } from './hooks/useAdminMasterclassListQueryParams';

gql`
  query AdminMasterClassListView(
    $paginationArgs: PaginationArgs!
    $orderByArgs: OrderByArgs
    $where: MasterClassWhereInput
    $clientsPaginationArgs: PaginationArgs
    $patientCasesPaginationArgs: PaginationArgs
    $accessLinksPaginationArgs: PaginationArgs
  ) {
    listMasterClasses(paginationArgs: $paginationArgs, orderByArgs: $orderByArgs, where: $where) {
      edges {
        node {
          ...MasterClassFields
          ...MasterClassRelationAccessLinks
          ...MasterClassRelationClients
          ...MasterClassRelationPatientCases
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

const PAGE_SIZE = 10;
export const masterClassListTablePrefix = 'masterClasses';

export function AdminMasterclassListClient() {
  const { paginationArgs, orderByArgs, where } = useAdminMasterClassListQueryParams(
    PAGE_SIZE,
    masterClassListTablePrefix
  );
  const masterClasses = useAdminMasterClassListViewQuery({
    variables: {
      paginationArgs,
      orderByArgs,
      where,
      clientsPaginationArgs: { first: 100 },
      accessLinksPaginationArgs: { first: 100 },
      patientCasesPaginationArgs: { first: 100 },
    },
  });
  const previousData = usePrevious(masterClasses.data);
  const pagination = usePaginationState(PAGE_SIZE, masterClassListTablePrefix);

  useEntitySubscription('MasterClass', {
    onCreate: {
      variables: {
        where,
      },
      onData: () => {
        void masterClasses.refetch();
      },
    },
    onUpdate: {
      variables: {
        where,
      },
      onData: () => {
        void masterClasses.refetch();
      },
    },
    onDelete: {
      variables: {},
      onData: ({ data }) => {
        const obj = data.data?.onDeleteMasterClass;
        if (!obj) {
          captureSentry({
            title: 'AdminMasterclassListClient | onData | onDeleteMasterclass | obj is undefined',
            detail: {},
          });
          return;
        }
        // TODO: Ideally we should either use a where inside the onDelete subscription variables
        // or check here that the deleted masterclass matches the where condition
        // but manual comparison is not an option and where on the serverside is problematic for a deleted object
        void masterClasses.refetch();
      },
    },
  });

  if (!previousData && !masterClasses.data) {
    return <PageLoading />;
  }
  return (
    <AdminMasterclassListView
      data={masterClasses.data || previousData!}
      pagination={pagination}
      tablePrefix={masterClassListTablePrefix}
    />
  );
}
